import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import { 
  Plus, 
  Minus, 
  Trash2, 
  ShoppingCart, 
  CreditCard, 
  Banknote, 
  Clock,
  Search,
  User,
  X,
  Calculator,
  CheckCircle,
  AlertTriangle,
  ArrowDownCircle,
  Receipt,
  Edit3,
  Unlock,
  DollarSign,
  List,
  Pencil,
  Wallet,
  RefreshCw,
  RotateCcw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format currency helper
const formatCurrencySimple = (value) => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0
  }).format(value || 0);
};

// Payment method badge with split details popover for mixto (click to open)
const PaymentMethodBadge = ({ sale }) => {
  const method = sale.payment_method;
  const split = sale.payment_split;
  
  const getBadgeStyle = (m) => {
    switch(m) {
      case 'efectivo': return 'bg-emerald-600/20 text-emerald-400';
      case 'tarjeta': return 'bg-blue-600/20 text-blue-400';
      case 'mixto': return 'bg-purple-600/20 text-purple-400 cursor-pointer hover:bg-purple-600/30';
      default: return 'bg-amber-600/20 text-amber-400';
    }
  };
  
  if (method === 'mixto' && split) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <span className={`px-2 py-0.5 rounded text-xs ${getBadgeStyle(method)}`}>
            mixto ▾
          </span>
        </PopoverTrigger>
        <PopoverContent side="top" className="bg-zinc-900 border-zinc-700 p-3 w-auto">
          <div className="space-y-1.5 text-xs">
            <p className="font-semibold text-white mb-2">Desglose del Pago:</p>
            {split.efectivo > 0 && (
              <div className="flex items-center justify-between gap-4">
                <span className="flex items-center gap-1.5 text-emerald-400">
                  <Banknote size={12} /> Efectivo
                </span>
                <span className="font-mono text-white">{formatCurrencySimple(split.efectivo)}</span>
              </div>
            )}
            {split.tarjeta > 0 && (
              <div className="flex items-center justify-between gap-4">
                <span className="flex items-center gap-1.5 text-blue-400">
                  <CreditCard size={12} /> Tarjeta
                </span>
                <span className="font-mono text-white">{formatCurrencySimple(split.tarjeta)}</span>
              </div>
            )}
            {split.deuda > 0 && (
              <div className="flex items-center justify-between gap-4">
                <span className="flex items-center gap-1.5 text-amber-400">
                  <Clock size={12} /> Deuda
                </span>
                <span className="font-mono text-white">{formatCurrencySimple(split.deuda)}</span>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }
  
  return (
    <span className={`px-2 py-0.5 rounded text-xs ${getBadgeStyle(method)}`}>
      {method}
    </span>
  );
};

const POS = () => {
  const { isAdmin, cashSession, checkCashSession, openCashSession, closeCashSession, hasOpenCashSession } = useAuth();
  const [products, setProducts] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cart, setCart] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [customDiscountInput, setCustomDiscountInput] = useState(''); // Separate state for custom input
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerMemberNumber, setCustomerMemberNumber] = useState('');
  const [customerMemberId, setCustomerMemberId] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  
  // Open cash session modal
  const [showOpenCashModal, setShowOpenCashModal] = useState(false);
  const [initialCash, setInitialCash] = useState('');
  const [openNotes, setOpenNotes] = useState('');
  
  // Close cash session modal
  const [showCloseCashModal, setShowCloseCashModal] = useState(false);
  const [countedCash, setCountedCash] = useState('');
  const [closeNotes, setCloseNotes] = useState('');
  
  // Operations modal
  const [showOperationsModal, setShowOperationsModal] = useState(false);
  const [operations, setOperations] = useState(null);
  const [loadingOperations, setLoadingOperations] = useState(false);
  const [editingSale, setEditingSale] = useState(null);  const [closingResult, setClosingResult] = useState(null);
  
  // Expense/Withdrawal modal
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [expenseType, setExpenseType] = useState('gasto');
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseDescription, setExpenseDescription] = useState('');
  const [expenseCategory, setExpenseCategory] = useState('');
  const [todayExpenses, setTodayExpenses] = useState([]);
  
  // Cash calculator modal
  const [showCalculator, setShowCalculator] = useState(false);
  const [calculatorTarget, setCalculatorTarget] = useState(null); // 'open' or 'close'
  const [billCounts, setBillCounts] = useState({
    b100: 0, b50: 0, b20: 0, b10: 0, b5: 0,
    c2: 0, c1: 0, c050: 0, c020: 0, c010: 0, c005: 0, c002: 0, c001: 0
  });
  
  // Split payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [splitPayment, setSplitPayment] = useState({
    efectivo: '',
    tarjeta: '',
    deuda: ''
  });
  
  // Refund modal
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundingSale, setRefundingSale] = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundProcessing, setRefundProcessing] = useState(false);

  useEffect(() => {
    fetchData();
    checkCashSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, subsRes, customersRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/subscriptions`),
        // Lite=true: skip N+1 enrichment (purchase totals, active subs). POS only needs
        // id, name, phone, email, member_number for the customer-search dropdown.
        axios.get(`${API}/members?status=socio&lite=true`).catch(() => ({ data: [] }))
      ]);
      setProducts(productsRes.data);
      setSubscriptions(subsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Combine products and subscriptions for search
  const allItems = useMemo(() => {
    const prods = products.map(p => ({ ...p, type: 'product', label: `${p.name} - ${p.brand}` }));
    const subs = subscriptions.map(s => ({ ...s, type: 'subscription', label: `${s.name} (${s.classes} clases)` }));
    return [...prods, ...subs];
  }, [products, subscriptions]);

  const filteredItems = useMemo(() => {
    if (!searchTerm) return allItems.slice(0, 10);
    const term = searchTerm.toLowerCase();
    return allItems.filter(item => 
      item.name.toLowerCase().includes(term) ||
      item.label.toLowerCase().includes(term) ||
      (item.brand && item.brand.toLowerCase().includes(term))
    ).slice(0, 10);
  }, [allItems, searchTerm]);

  const filteredCustomers = useMemo(() => {
    if (!customerSearch) return customers.slice(0, 8);
    const term = customerSearch.toLowerCase();
    return customers.filter(c => 
      (c.name || '').toLowerCase().includes(term) ||
      (c.phone || '').toLowerCase().includes(term) ||
      (c.member_number || '').toLowerCase().includes(term)
    ).slice(0, 8);
  }, [customers, customerSearch]);

  const addToCart = (item) => {
    const type = item.type;
    const existingIndex = cart.findIndex(
      (c) => c.item_id === item.id && c.item_type === type
    );

    if (existingIndex >= 0) {
      const newCart = [...cart];
      if (type === 'product') {
        const product = products.find(p => p.id === item.id);
        if (newCart[existingIndex].quantity >= product.stock) {
          toast.error('Stock insuficiente');
          return;
        }
      }
      newCart[existingIndex].quantity += 1;
      newCart[existingIndex].subtotal = newCart[existingIndex].quantity * newCart[existingIndex].unit_price;
      setCart(newCart);
    } else {
      if (type === 'product' && item.stock < 1) {
        toast.error('Producto sin stock');
        return;
      }
      setCart([
        ...cart,
        {
          item_type: type,
          item_id: item.id,
          item_name: item.name,
          quantity: 1,
          unit_price: item.price,
          subtotal: item.price
        }
      ]);
    }
    toast.success(`${item.name} agregado`);
    setSearchTerm('');
    setShowItemDropdown(false);
  };

  const selectCustomer = (customer) => {
    setCustomerName(customer.name);
    setCustomerPhone(customer.phone || '');
    setCustomerMemberNumber(customer.member_number || '');
    setCustomerMemberId(customer.id || '');
    setCustomerSearch('');
    setShowCustomerDropdown(false);
  };

  const clearCustomer = () => {
    setCustomerName('');
    setCustomerPhone('');
    setCustomerMemberNumber('');
    setCustomerMemberId('');
    setCustomerSearch('');
  };

  const updateQuantity = (index, delta) => {
    const newCart = [...cart];
    const newQty = newCart[index].quantity + delta;
    
    if (newQty < 1) {
      removeFromCart(index);
      return;
    }

    if (newCart[index].item_type === 'product') {
      const product = products.find(p => p.id === newCart[index].item_id);
      if (newQty > product.stock) {
        toast.error('Stock insuficiente');
        return;
      }
    }

    newCart[index].quantity = newQty;
    newCart[index].subtotal = newQty * newCart[index].unit_price;
    setCart(newCart);
  };

  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  const subtotal = cart.reduce((sum, item) => sum + item.subtotal, 0);
  const discountAmount = subtotal * (discount / 100);
  const total = subtotal - discountAmount;

  // Open payment modal
  const openPaymentModal = () => {
    if (cart.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }
    // Reset split payment values
    setSplitPayment({ efectivo: '', tarjeta: '', deuda: '' });
    setShowPaymentModal(true);
  };

  // Quick pay with single method
  const quickPay = async (paymentMethod) => {
    if (cart.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }
    await processSale(paymentMethod, null);
  };

  // Process sale with optional split payment
  const processSale = async (paymentMethod, paymentSplit) => {
    // Use customerSearch if customerName is not set
    const finalCustomerName = customerName || customerSearch.trim();

    // Check if payment includes debt
    const hasDebt = paymentMethod === 'deuda' || (paymentSplit && parseFloat(paymentSplit.deuda || 0) > 0);
    if (hasDebt && !finalCustomerName) {
      toast.error('Para deuda se requiere nombre del cliente');
      return;
    }

    // Check if cart contains subscriptions - require registered member
    const hasSubscription = cart.some(item => item.item_type === 'subscription');
    if (hasSubscription) {
      // A registered member is identified by having a member_number (the green "Socio registrado" badge).
      // Phone is optional now, so we cannot use it as a registration flag anymore.
      if (!customerMemberNumber) {
        toast.error('Las suscripciones solo se venden a socios registrados. Selecciona un socio del listado o créalo en la sección Socios.');
        return;
      }
    }

    setProcessing(true);
    try {
      const payload = {
        items: cart,
        payment_method: paymentMethod,
        discount_percent: discount,
        customer_name: finalCustomerName || null,
        customer_phone: customerPhone || null,
        member_id: customerMemberId || null,
        notes: notes || null
      };

      // Add payment split if provided
      if (paymentSplit) {
        payload.payment_split = paymentSplit;
      }

      await axios.post(`${API}/sales`, payload);

      toast.success('¡Venta registrada!');
      setCart([]);
      setDiscount(0);
      clearCustomer();
      setNotes('');
      setShowPaymentModal(false);
      fetchData();
      checkCashSession(); // Refresh session data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar venta');
    } finally {
      setProcessing(false);
    }
  };

  // Handle split payment submission
  const handleSplitPayment = async () => {
    const efectivo = parseFloat(splitPayment.efectivo || 0);
    const tarjeta = parseFloat(splitPayment.tarjeta || 0);
    const deuda = parseFloat(splitPayment.deuda || 0);
    const splitTotal = efectivo + tarjeta + deuda;

    if (Math.abs(splitTotal - total) > 0.01) {
      toast.error(`El total del pago (${formatCurrency(splitTotal)}) no coincide con el total de la venta (${formatCurrency(total)})`);
      return;
    }

    // Determine payment method
    const methodsUsed = [];
    if (efectivo > 0) methodsUsed.push('efectivo');
    if (tarjeta > 0) methodsUsed.push('tarjeta');
    if (deuda > 0) methodsUsed.push('deuda');

    let paymentMethod = methodsUsed.length > 1 ? 'mixto' : methodsUsed[0] || 'efectivo';

    await processSale(paymentMethod, { efectivo, tarjeta, deuda });
  };

  // Auto-fill remaining amount
  const fillRemaining = (field) => {
    const efectivo = field === 'efectivo' ? 0 : parseFloat(splitPayment.efectivo || 0);
    const tarjeta = field === 'tarjeta' ? 0 : parseFloat(splitPayment.tarjeta || 0);
    const deuda = field === 'deuda' ? 0 : parseFloat(splitPayment.deuda || 0);
    const remaining = Math.max(0, total - efectivo - tarjeta - deuda);
    
    setSplitPayment(prev => ({
      ...prev,
      [field]: remaining.toFixed(2)
    }));
  };

  // Calculator functions
  const billValues = {
    b100: 100, b50: 50, b20: 20, b10: 10, b5: 5,
    c2: 2, c1: 1, c050: 0.50, c020: 0.20, c010: 0.10, c005: 0.05, c002: 0.02, c001: 0.01
  };

  const calculateTotal = () => {
    return Object.entries(billCounts).reduce((sum, [key, count]) => {
      return sum + (billValues[key] * count);
    }, 0);
  };

  const openCalculator = (target) => {
    setBillCounts({ b100: 0, b50: 0, b20: 0, b10: 0, b5: 0, c2: 0, c1: 0, c050: 0, c020: 0, c010: 0, c005: 0, c002: 0, c001: 0 });
    setCalculatorTarget(target);
    setShowCalculator(true);
  };

  const applyCalculatorResult = () => {
    const total = calculateTotal().toFixed(2);
    if (calculatorTarget === 'open') {
      setInitialCash(total);
    } else if (calculatorTarget === 'close') {
      setCountedCash(total);
    }
    setShowCalculator(false);
  };

  const updateBillCount = (key, delta) => {
    setBillCounts(prev => ({
      ...prev,
      [key]: Math.max(0, prev[key] + delta)
    }));
  };

  // Open cash session
  const handleOpenCash = async () => {
    if (!initialCash || parseFloat(initialCash) < 0) {
      toast.error('Ingresa el efectivo inicial');
      return;
    }

    try {
      await openCashSession(parseFloat(initialCash), openNotes || null);
      toast.success('¡Caja abierta!');
      setShowOpenCashModal(false);
      setInitialCash('');
      setOpenNotes('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al abrir caja');
    }
  };

  // Close cash session
  const openCloseCashModal = async () => {
    await checkCashSession();
    const session = cashSession?.session;
    if (session) {
      setCountedCash(session.current_expected_cash?.toString() || '0');
    }
    setCloseNotes('');
    setClosingResult(null);
    setShowCloseCashModal(true);
  };

  const handleCloseCash = async () => {
    if (!countedCash || parseFloat(countedCash) < 0) {
      toast.error('Ingresa el efectivo contado');
      return;
    }

    try {
      const result = await closeCashSession(parseFloat(countedCash), closeNotes || null);
      setClosingResult(result);
      toast.success('¡Caja cerrada!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar caja');
    }
  };

  // Operations functions
  const loadOperations = async () => {
    const sessionId = cashSession?.session?.id;
    if (!sessionId) return;
    
    setLoadingOperations(true);
    try {
      const response = await axios.get(`${API}/sales/by-session/${sessionId}`);
      setOperations(response.data);
    } catch (error) {
      toast.error('Error al cargar operaciones');
    } finally {
      setLoadingOperations(false);
    }
  };

  const openOperationsModal = async () => {
    setShowOperationsModal(true);
    await loadOperations();
  };

  // Open refund modal
  const openRefundModal = (sale) => {
    setRefundingSale(sale);
    setRefundReason('');
    setShowRefundModal(true);
  };

  // Process refund
  const handleRefundSale = async () => {
    if (!refundingSale) return;
    setRefundProcessing(true);
    try {
      await axios.post(`${API}/sales/${refundingSale.id}/refund`, {
        reason: refundReason || null
      });
      toast.success('Devolución procesada correctamente');
      setShowRefundModal(false);
      setRefundingSale(null);
      setRefundReason('');
      await loadOperations();
      checkCashSession();
      fetchData();
    } catch (error) {
      console.error('Error processing refund:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar devolución');
    } finally {
      setRefundProcessing(false);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('¿Eliminar este gasto?')) return;
    try {
      await axios.delete(`${API}/expenses/${expenseId}`);
      toast.success('Gasto eliminado');
      await loadOperations();
      checkCashSession();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleUpdateSale = async () => {
    if (!editingSale) return;
    try {
      await axios.put(`${API}/sales/${editingSale.id}`, {
        customer_name: editingSale.customer_name,
        notes: editingSale.notes,
        discount_percent: editingSale.discount_percent
      });
      toast.success('Venta actualizada');
      setEditingSale(null);
      await loadOperations();
      checkCashSession();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  // Expense functions
  const openExpenseModal = async () => {
    try {
      const response = await axios.get(`${API}/expenses/today`);
      setTodayExpenses(response.data.expenses || []);
    } catch (error) {
      console.error('Error loading expenses');
    }
    setExpenseType('gasto');
    setExpenseAmount('');
    setExpenseDescription('');
    setExpenseCategory('');
    setShowExpenseModal(true);
  };

  const submitExpense = async () => {
    if (!expenseAmount || parseFloat(expenseAmount) <= 0) {
      toast.error('Ingresa un monto válido');
      return;
    }
    if (!expenseDescription.trim()) {
      toast.error('Ingresa una descripción');
      return;
    }

    try {
      await axios.post(`${API}/expenses`, {
        type: expenseType,
        amount: parseFloat(expenseAmount),
        description: expenseDescription,
        category: expenseCategory || null
      });
      toast.success(`${expenseType === 'gasto' ? 'Gasto' : 'Retiro'} registrado`);
      
      const response = await axios.get(`${API}/expenses/today`);
      setTodayExpenses(response.data.expenses || []);
      checkCashSession(); // Refresh session
      
      setExpenseAmount('');
      setExpenseDescription('');
      setExpenseCategory('');
    } catch (error) {
      toast.error('Error al registrar');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const discountOptions = [0, 5, 10, 15, 20];
  const expenseCategories = ['Luz', 'Agua', 'Internet', 'Limpieza', 'Suministros', 'Mantenimiento', 'Otro'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]" />
      </div>
    );
  }

  const session = cashSession?.session;

  // If no open cash session, show open cash modal
  if (!hasOpenCashSession()) {
    return (
      <div data-testid="pos-page" className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <Card className="bg-[#0A0A0A] border-zinc-800 max-w-md w-full mx-4">
          <CardHeader className="text-center pb-2">
            <div className="w-20 h-20 mx-auto bg-[#FF3B30]/10 rounded-full flex items-center justify-center mb-4">
              <Unlock size={40} className="text-[#FF3B30]" />
            </div>
            <CardTitle className="text-white text-xl">Abrir Caja</CardTitle>
            <p className="text-zinc-500 text-sm mt-2">
              Para usar el punto de venta, primero debes abrir la caja indicando el efectivo inicial
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-zinc-400">Efectivo Inicial en Caja</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => openCalculator('open')}
                  className="h-8 px-2 text-amber-400 hover:text-amber-300 hover:bg-amber-400/10"
                >
                  <Calculator size={16} className="mr-1" />
                  Contar
                </Button>
              </div>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
                <Input
                  data-testid="initial-cash-input"
                  type="number"
                  step="0.01"
                  value={initialCash}
                  onChange={(e) => setInitialCash(e.target.value)}
                  placeholder="0.00"
                  className="pl-10 bg-zinc-900 border-zinc-800 text-xl font-mono h-14"
                  autoFocus
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 mb-2 block">Notas (opcional)</Label>
              <Input
                value={openNotes}
                onChange={(e) => setOpenNotes(e.target.value)}
                placeholder="Observaciones de apertura..."
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
            <Button
              data-testid="open-cash-btn"
              onClick={handleOpenCash}
              className="w-full h-12 bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-bold text-lg"
            >
              Abrir Caja
            </Button>
          </CardContent>
        </Card>
        
        {/* Calculator Modal for Open Cash */}
        {renderCalculatorModal()}
      </div>
    );
  }

  return (
    <div data-testid="pos-page" className="min-h-[calc(100vh-2rem)]">
      {/* Header with session info and action buttons */}
      <div className="flex flex-col gap-3 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-white">Punto de Venta</h1>
            {session && (
              <p className="text-zinc-500 text-sm mt-1">
                Caja abierta: <span className="text-emerald-400 font-mono">{formatCurrency(session.initial_cash)}</span> inicial
                {' • '}
                Esperado: <span className="text-white font-mono">{formatCurrency(session.current_expected_cash)}</span>
              </p>
            )}
          </div>
          
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              onClick={fetchData}
              variant="outline"
              size="sm"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              <RefreshCw size={16} />
            </Button>
            
            <Button
              data-testid="expense-btn"
              onClick={openExpenseModal}
              className="flex-1 sm:flex-none bg-amber-600 hover:bg-amber-700 text-white font-bold"
            >
              <ArrowDownCircle size={18} className="mr-2" />
              <span className="hidden sm:inline">Gasto / Retiro</span>
              <span className="sm:hidden">Gasto</span>
            </Button>
            
            <Button
              data-testid="close-cash-btn"
              onClick={openCloseCashModal}
              className="flex-1 sm:flex-none bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-bold primary-glow"
            >
              <Calculator size={18} className="mr-2" />
              Cierre de Caja
            </Button>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:gap-6">
        {/* Search Section */}
        <div className="lg:col-span-3 space-y-4">
          {/* Item Search */}
          <Card className="bg-[#0A0A0A] border-zinc-800">
            <CardContent className="p-4">
              <Label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">
                Buscar Producto o Suscripción
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <Input
                  data-testid="item-search"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowItemDropdown(true);
                  }}
                  onFocus={() => setShowItemDropdown(true)}
                  placeholder="Escribe para buscar..."
                  className="pl-10 bg-[#050505] border-zinc-800 focus:border-[#FF3B30] h-12 text-base"
                />
                {showItemDropdown && filteredItems.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-[#0A0A0A] border border-zinc-800 rounded-sm shadow-xl max-h-72 overflow-y-auto">
                    {filteredItems.map((item) => (
                      <button
                        key={`${item.type}-${item.id}`}
                        data-testid={`search-item-${item.id}`}
                        onClick={() => addToCart(item)}
                        className="w-full px-4 py-3 text-left hover:bg-zinc-800 flex justify-between items-center border-b border-zinc-800/50 last:border-0"
                      >
                        <div>
                          <p className="text-white font-medium text-sm">{item.name}</p>
                          <p className="text-xs text-zinc-500">
                            {item.type === 'product' ? `${item.brand} • Stock: ${item.stock}` : `${item.classes} clases`}
                          </p>
                        </div>
                        <span className="text-[#FF3B30] font-mono font-bold">
                          {formatCurrency(item.price)}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {showItemDropdown && (
                <button 
                  onClick={() => setShowItemDropdown(false)}
                  className="fixed inset-0 z-10"
                />
              )}
            </CardContent>
          </Card>

          {/* Customer Search */}
          <Card className="bg-[#0A0A0A] border-zinc-800">
            <CardContent className="p-4">
              <Label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">
                Cliente / Socio
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <Input
                  data-testid="customer-search"
                  value={customerName ? `${customerMemberNumber ? customerMemberNumber + ' · ' : ''}${customerName}` : customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    setCustomerName('');
                    setCustomerPhone('');
                    setCustomerMemberNumber('');
                    setCustomerMemberId('');
                    setShowCustomerDropdown(true);
                  }}
                  onFocus={() => setShowCustomerDropdown(true)}
                  placeholder="Buscar socio por nombre, teléfono o nº..."
                  className="pl-10 pr-10 bg-[#050505] border-zinc-800 focus:border-[#FF3B30] h-12 text-base"
                />
                {customerName && (
                  <button
                    onClick={clearCustomer}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                    data-testid="clear-customer-btn"
                  >
                    <X size={18} />
                  </button>
                )}
                {showCustomerDropdown && !customerName && filteredCustomers.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-[#0A0A0A] border border-zinc-800 rounded-sm shadow-xl max-h-60 overflow-y-auto">
                    {filteredCustomers.map((customer, idx) => (
                      <button
                        key={customer.id || idx}
                        data-testid={`customer-option-${idx}`}
                        onClick={() => selectCustomer(customer)}
                        className="w-full px-4 py-3 text-left hover:bg-zinc-800 flex justify-between items-center border-b border-zinc-800/50 last:border-0"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            {customer.member_number && (
                              <span className="text-[10px] font-mono bg-emerald-900/40 text-emerald-300 px-1.5 py-0.5 rounded">
                                {customer.member_number}
                              </span>
                            )}
                            <p className="text-white font-medium text-sm truncate">{customer.name}</p>
                          </div>
                          {customer.phone && (
                            <p className="text-xs text-zinc-500 mt-0.5">{customer.phone}</p>
                          )}
                        </div>
                      </button>
                    ))}
                    {customerSearch && !filteredCustomers.find(c => (c.name || '').toLowerCase() === customerSearch.toLowerCase()) && (
                      cart.some(i => i.item_type === 'subscription') ? (
                        <div className="w-full px-4 py-3 text-left text-xs text-amber-400 border-t border-zinc-700 bg-amber-900/10">
                          ⚠️ Las suscripciones solo pueden venderse a socios registrados. Crea el socio en la sección "Socios" primero.
                        </div>
                      ) : (
                        <button
                          onClick={() => { setCustomerName(customerSearch); setCustomerPhone(''); setCustomerMemberNumber(''); setCustomerMemberId(''); setShowCustomerDropdown(false); }}
                          className="w-full px-4 py-3 text-left hover:bg-zinc-800 text-emerald-400 border-t border-zinc-700"
                          data-testid="use-as-new-customer-btn"
                        >
                          + Usar &quot;{customerSearch}&quot; como cliente ocasional (sin socio)
                        </button>
                      )
                    )}
                  </div>
                )}
              </div>
              {customerName && customerMemberNumber && (
                <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1">
                  <span className="font-mono">{customerMemberNumber}</span> · Socio registrado
                  {customerPhone && <span className="text-zinc-500">· {customerPhone}</span>}
                </p>
              )}
              {customerName && !customerMemberNumber && (
                <p className="text-xs text-amber-400 mt-2">
                  Cliente ocasional (no es socio registrado). Las suscripciones requieren socio.
                </p>
              )}
              {showCustomerDropdown && !customerName && (
                <button 
                  onClick={() => setShowCustomerDropdown(false)}
                  className="fixed inset-0 z-10"
                />
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="bg-[#0A0A0A] border-zinc-800">
            <CardContent className="p-4">
              <Label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">Notas</Label>
              <Input
                data-testid="sale-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notas opcionales..."
                className="bg-[#050505] border-zinc-800 focus:border-[#FF3B30] h-10"
              />
            </CardContent>
          </Card>
        </div>

        {/* Cart Section */}
        <div className="lg:col-span-2">
          <Card className="bg-[#0A0A0A] border-zinc-800 h-full flex flex-col">
            <CardHeader className="border-b border-zinc-800 pb-3 sm:pb-4 px-4">
              <CardTitle className="flex items-center gap-2 text-white text-base sm:text-lg">
                <ShoppingCart size={18} />
                Carrito
                {cart.length > 0 && (
                  <span className="ml-auto bg-[#FF3B30] text-white text-xs px-2 py-1 rounded-sm">
                    {cart.length}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            
            <CardContent className="flex-1 flex flex-col p-3 sm:p-4 min-h-0">
              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto space-y-2 mb-3 sm:mb-4 max-h-48 sm:max-h-64">
                {cart.length === 0 ? (
                  <div className="text-center text-zinc-500 py-6 sm:py-8">
                    <ShoppingCart size={36} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Carrito vacío</p>
                  </div>
                ) : (
                  cart.map((item, index) => (
                    <div
                      key={index}
                      data-testid={`cart-item-${index}`}
                      className="flex items-center gap-2 sm:gap-3 bg-zinc-900/50 p-2 sm:p-3 rounded-sm"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-white truncate">{item.item_name}</p>
                        <p className="text-[10px] sm:text-xs text-zinc-500">{formatCurrency(item.unit_price)} c/u</p>
                      </div>
                      <div className="flex items-center gap-1 sm:gap-2">
                        <button
                          onClick={() => updateQuantity(index, -1)}
                          className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 rounded-sm transition-fast"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="w-6 sm:w-8 text-center font-mono text-xs sm:text-sm">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(index, 1)}
                          className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 rounded-sm transition-fast"
                        >
                          <Plus size={12} />
                        </button>
                        <button
                          onClick={() => removeFromCart(index)}
                          className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center text-red-500 hover:bg-red-500/10 rounded-sm transition-fast"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                      <p className="font-mono font-bold text-white text-xs sm:text-sm w-16 sm:w-20 text-right">
                        {formatCurrency(item.subtotal)}
                      </p>
                    </div>
                  ))
                )}
              </div>

              {/* Discount Selection */}
              <div className="mb-3 sm:mb-4">
                <Label className="text-[10px] sm:text-xs text-zinc-500 mb-1 sm:mb-2 block">Descuento</Label>
                <div className="flex gap-1 sm:gap-2">
                  {discountOptions.map((d) => (
                    <button
                      key={d}
                      data-testid={`discount-${d}`}
                      onClick={() => {
                        setDiscount(d);
                        setCustomDiscountInput(''); // Clear custom input when selecting preset
                      }}
                      className={`flex-1 py-1.5 sm:py-2 rounded-sm text-[10px] sm:text-xs font-medium transition-fast ${
                        discount === d
                          ? 'bg-[#FF3B30] text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {d}%
                    </button>
                  ))}
                  {/* Custom discount input — admins up to 100%, vendedores up to 30% */}
                  {(() => {
                    const maxDiscount = isAdmin() ? 100 : 30;
                    return (
                    <div className="flex-1 relative min-w-[60px]">
                      <input
                        type="number"
                        min="0"
                        max={maxDiscount}
                        placeholder="Otro"
                        value={customDiscountInput}
                        onChange={(e) => {
                          setCustomDiscountInput(e.target.value);
                        }}
                        onBlur={(e) => {
                          const val = Math.min(maxDiscount, Math.max(0, parseInt(e.target.value) || 0));
                          setDiscount(val);
                          setCustomDiscountInput(val > 0 && !discountOptions.includes(val) ? val.toString() : '');
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const val = Math.min(maxDiscount, Math.max(0, parseInt(e.target.value) || 0));
                            setDiscount(val);
                            setCustomDiscountInput(val > 0 && !discountOptions.includes(val) ? val.toString() : '');
                            e.target.blur();
                          }
                        }}
                        onFocus={() => {
                          // When focusing, if there's a custom discount, show it
                          if (!discountOptions.includes(discount) && discount > 0) {
                            setCustomDiscountInput(discount.toString());
                          }
                        }}
                        title={isAdmin() ? 'Descuento personalizado (0-100%)' : 'Descuento personalizado (máx. 30%)'}
                        className={`w-full py-1.5 sm:py-2 px-1 pr-4 rounded-sm text-[10px] sm:text-xs font-medium text-center transition-fast appearance-none ${
                          !discountOptions.includes(discount) && discount > 0
                            ? 'bg-[#FF3B30] text-white'
                            : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        }`}
                        style={{ MozAppearance: 'textfield' }}
                        data-testid="discount-custom"
                      />
                      <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] text-zinc-500 pointer-events-none">%</span>
                    </div>
                    );
                  })()}
                </div>
              </div>

              {/* Totals */}
              <div className="space-y-1 sm:space-y-2 mb-3 sm:mb-4 pb-3 sm:pb-4 border-b border-zinc-800">
                <div className="flex justify-between text-xs sm:text-sm text-zinc-400">
                  <span>Subtotal</span>
                  <span className="font-mono">{formatCurrency(subtotal)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-xs sm:text-sm text-emerald-500">
                    <span>Descuento ({discount}%)</span>
                    <span className="font-mono">-{formatCurrency(discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg sm:text-xl font-bold text-white">
                  <span>Total</span>
                  <span className="font-mono text-[#FF3B30]">{formatCurrency(total)}</span>
                </div>
              </div>

              {/* Payment Buttons */}
              <div className="grid grid-cols-4 gap-2">
                <Button
                  type="button"
                  data-testid="pay-cash-btn"
                  onClick={() => quickPay('efectivo')}
                  disabled={processing || cart.length === 0}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex flex-col items-center py-3 sm:py-4 h-auto rounded-sm"
                >
                  <Banknote size={18} />
                  <span className="text-[10px] sm:text-xs mt-1">Efectivo</span>
                </Button>
                <Button
                  type="button"
                  data-testid="pay-card-btn"
                  onClick={() => quickPay('tarjeta')}
                  disabled={processing || cart.length === 0}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold flex flex-col items-center py-3 sm:py-4 h-auto rounded-sm"
                >
                  <CreditCard size={18} />
                  <span className="text-[10px] sm:text-xs mt-1">Tarjeta</span>
                </Button>
                <Button
                  type="button"
                  data-testid="pay-debt-btn"
                  onClick={() => quickPay('deuda')}
                  disabled={processing || cart.length === 0}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold flex flex-col items-center py-3 sm:py-4 h-auto rounded-sm"
                >
                  <Clock size={18} />
                  <span className="text-[10px] sm:text-xs mt-1">Deuda</span>
                </Button>
                <Button
                  type="button"
                  data-testid="pay-split-btn"
                  onClick={openPaymentModal}
                  disabled={processing || cart.length === 0}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-bold flex flex-col items-center py-3 sm:py-4 h-auto rounded-sm"
                >
                  <Wallet size={18} />
                  <span className="text-[10px] sm:text-xs mt-1">Dividir</span>
                </Button>
              </div>
              
              {/* Operations Button */}
              <Button
                type="button"
                data-testid="operations-btn"
                onClick={openOperationsModal}
                variant="outline"
                className="w-full mt-3 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                <List size={16} className="mr-2" />
                OPERACIONES
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ============ OPERATIONS MODAL ============ */}
      <Dialog open={showOperationsModal} onOpenChange={setShowOperationsModal}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-5xl w-[95vw] sm:w-[90vw] mx-2 sm:mx-4 max-h-[90vh] overflow-hidden flex flex-col z-[100]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <List size={20} />
              Operaciones de la Caja Actual
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto space-y-4 py-4 pr-2">
            {loadingOperations ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]" />
              </div>
            ) : operations ? (
              <>
                {/* Cash Session Info */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-amber-600/10 border border-amber-600/30 rounded-sm p-3">
                    <p className="text-xs text-amber-400 uppercase">Monto Apertura</p>
                    <p className="text-xl font-mono font-bold text-amber-400">
                      {formatCurrency(cashSession?.session?.initial_cash || 0)}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">
                      {cashSession?.session?.opened_at ? new Date(cashSession.session.opened_at).toLocaleString('es-ES') : '-'}
                    </p>
                  </div>
                  <div className="bg-emerald-600/10 border border-emerald-600/30 rounded-sm p-3">
                    <p className="text-xs text-emerald-400 uppercase">Efectivo Esperado Actual</p>
                    <p className="text-xl font-mono font-bold text-emerald-400">
                      {formatCurrency(cashSession?.session?.current_expected_cash || cashSession?.session?.initial_cash || 0)}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">Estado: {cashSession?.session?.status === 'open' ? 'Abierta' : 'Cerrada'}</p>
                  </div>
                </div>

                {/* Sales */}
                {operations.sales?.length > 0 && (
                  <div>
                    <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                      <ShoppingCart size={16} className="text-emerald-400" />
                      Ventas ({operations.sales.length})
                    </h4>
                    <div className="bg-zinc-900 rounded-sm overflow-x-auto">
                      <table className="w-full text-sm min-w-[700px]">
                        <thead>
                          <tr className="bg-zinc-800/50">
                            <th className="text-left p-2 text-zinc-400 w-16">Hora</th>
                            <th className="text-left p-2 text-zinc-400">Cliente</th>
                            <th className="text-left p-2 text-zinc-400 w-24">Pago</th>
                            <th className="text-right p-2 text-zinc-400 w-20">Subtotal</th>
                            <th className="text-right p-2 text-zinc-400 w-20">Desc.</th>
                            <th className="text-right p-2 text-zinc-400 w-20">Total</th>
                            <th className="text-center p-2 text-zinc-400 w-20">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {operations.sales.map(sale => (
                            <tr key={sale.id} className={`border-t border-zinc-800 ${sale.refunded ? 'opacity-50' : ''}`}>
                              <td className="p-2 text-zinc-300 whitespace-nowrap">
                                {new Date(sale.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                              </td>
                              <td className="p-2 text-white">
                                {sale.customer_name || '-'}
                                {sale.refunded && <span className="ml-2 text-xs text-red-400">(Devuelta)</span>}
                              </td>
                              <td className="p-2">
                                <PaymentMethodBadge sale={sale} />
                              </td>
                              <td className="p-2 text-right font-mono text-zinc-300">
                                {sale.refunded ? <s>{formatCurrency(sale.subtotal)}</s> : formatCurrency(sale.subtotal)}
                              </td>
                              <td className="p-2 text-right font-mono">
                                {(sale.descuento_monto || sale.discount_amount || 0) > 0 ? (
                                  <span className="text-amber-400">
                                    -{formatCurrency(sale.descuento_monto || sale.discount_amount)}
                                    {(sale.descuento_porcentaje || sale.discount_percent) > 0 && (
                                      <span className="text-xs ml-1">({sale.descuento_porcentaje || sale.discount_percent}%)</span>
                                    )}
                                  </span>
                                ) : (
                                  <span className="text-zinc-500">-</span>
                                )}
                              </td>
                              <td className="p-2 text-right font-mono text-emerald-400">
                                {sale.refunded ? <s>{formatCurrency(sale.total_final || sale.total)}</s> : formatCurrency(sale.total_final || sale.total)}
                              </td>
                              <td className="p-2 text-center">
                                {!sale.refunded && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => openRefundModal(sale)} 
                                    className="h-7 w-7 p-0 text-amber-400 hover:text-amber-300"
                                    title="Devolver venta"
                                  >
                                    <RotateCcw size={14} />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Expenses */}
                {operations.expenses?.length > 0 && (
                  <div>
                    <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                      <Wallet size={16} className="text-red-400" />
                      Gastos y Retiros ({operations.expenses.length})
                    </h4>
                    <div className="bg-zinc-900 rounded-sm overflow-x-auto">
                      <table className="w-full text-sm min-w-[500px]">
                        <thead>
                          <tr className="bg-zinc-800/50">
                            <th className="text-left p-2 text-zinc-400">Hora</th>
                            <th className="text-left p-2 text-zinc-400">Tipo</th>
                            <th className="text-left p-2 text-zinc-400">Descripción</th>
                            <th className="text-right p-2 text-zinc-400">Monto</th>
                            <th className="text-center p-2 text-zinc-400">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {operations.expenses.map(expense => (
                            <tr key={expense.id} className="border-t border-zinc-800">
                              <td className="p-2 text-zinc-300">
                                {new Date(expense.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                              </td>
                              <td className="p-2">
                                <span className={`px-2 py-0.5 rounded text-xs ${
                                  expense.type === 'gasto' ? 'bg-red-600/20 text-red-400' : 'bg-purple-600/20 text-purple-400'
                                }`}>
                                  {expense.type === 'gasto' ? 'Gasto' : 'Retiro'}
                                </span>
                              </td>
                              <td className="p-2 text-white">{expense.description}</td>
                              <td className="p-2 text-right font-mono text-red-400">-{formatCurrency(expense.amount)}</td>
                              <td className="p-2 text-center">
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteExpense(expense.id)} className="h-7 w-7 p-0 text-red-400">
                                  <Trash2 size={14} />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Debt Payments */}
                {operations.debt_payments?.length > 0 && (
                  <div>
                    <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                      <CreditCard size={16} className="text-blue-400" />
                      Cobros de Deudas ({operations.debt_payments.length})
                    </h4>
                    <div className="bg-zinc-900 rounded-sm overflow-x-auto">
                      <table className="w-full text-sm min-w-[400px]">
                        <thead>
                          <tr className="bg-zinc-800/50">
                            <th className="text-left p-2 text-zinc-400">Hora</th>
                            <th className="text-left p-2 text-zinc-400">Método</th>
                            <th className="text-right p-2 text-zinc-400">Monto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {operations.debt_payments.map(payment => (
                            <tr key={payment.id} className="border-t border-zinc-800">
                              <td className="p-2 text-zinc-300">
                                {new Date(payment.date).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                              </td>
                              <td className="p-2 text-zinc-400">{payment.method}</td>
                              <td className="p-2 text-right font-mono text-blue-400">+{formatCurrency(payment.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Refunds */}
                {operations.refunds?.length > 0 && (
                  <div>
                    <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                      <RotateCcw size={16} className="text-amber-400" />
                      Devoluciones ({operations.refunds.length})
                    </h4>
                    <div className="bg-zinc-900 rounded-sm overflow-x-auto">
                      <table className="w-full text-sm min-w-[500px]">
                        <thead>
                          <tr className="bg-zinc-800/50">
                            <th className="text-left p-2 text-zinc-400">Hora</th>
                            <th className="text-left p-2 text-zinc-400">Venta Original</th>
                            <th className="text-left p-2 text-zinc-400">Razón</th>
                            <th className="text-right p-2 text-zinc-400">Monto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {operations.refunds.map(refund => (
                            <tr key={refund.id} className="border-t border-zinc-800">
                              <td className="p-2 text-zinc-300">
                                {new Date(refund.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                              </td>
                              <td className="p-2 text-white">
                                {refund.items?.map(i => `${i.quantity}x ${i.item_name}`).join(', ') || 'Venta'}
                              </td>
                              <td className="p-2 text-zinc-400">{refund.reason || '-'}</td>
                              <td className="p-2 text-right font-mono text-red-400">-{formatCurrency(refund.refund_amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {!operations.sales?.length && !operations.expenses?.length && !operations.debt_payments?.length && !operations.refunds?.length && (
                  <p className="text-zinc-500 text-center py-8">No hay operaciones en esta caja</p>
                )}
                {/* Mini-resumen por método de pago */}
                {(operations.sales?.length > 0 || operations.debt_payments?.length > 0 || operations.refunds?.length > 0) && (
                  <div className="bg-zinc-900/50 rounded-sm p-3 border border-zinc-800 mt-4">
                    <h4 className="text-xs text-zinc-500 uppercase tracking-wider mb-3 font-semibold">Resumen de la Caja</h4>
                    <div className="grid grid-cols-5 gap-3">
                      <div className="text-center">
                        <p className="text-[10px] text-zinc-500 mb-1">Efectivo</p>
                        <p className="text-sm font-mono text-emerald-400">
                          {formatCurrency(
                            (operations.sales?.filter(s => !s.refunded && s.payment_method === 'efectivo').reduce((sum, s) => sum + s.total, 0) || 0) +
                            (operations.sales?.filter(s => !s.refunded && s.payment_method === 'mixto').reduce((sum, s) => sum + (s.payment_split?.efectivo || 0), 0) || 0) +
                            (operations.debt_payments?.filter(p => p.method === 'efectivo').reduce((sum, p) => sum + p.amount, 0) || 0) -
                            (operations.refunds?.filter(r => r.payment_method === 'efectivo').reduce((sum, r) => sum + r.refund_amount, 0) || 0) -
                            (operations.refunds?.filter(r => r.payment_method === 'mixto').reduce((sum, r) => sum + (r.payment_split?.efectivo || 0), 0) || 0)
                          )}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-[10px] text-zinc-500 mb-1">Tarjeta</p>
                        <p className="text-sm font-mono text-blue-400">
                          {formatCurrency(
                            (operations.sales?.filter(s => !s.refunded && s.payment_method === 'tarjeta').reduce((sum, s) => sum + s.total, 0) || 0) +
                            (operations.sales?.filter(s => !s.refunded && s.payment_method === 'mixto').reduce((sum, s) => sum + (s.payment_split?.tarjeta || 0), 0) || 0) +
                            (operations.debt_payments?.filter(p => p.method === 'tarjeta').reduce((sum, p) => sum + p.amount, 0) || 0) -
                            (operations.refunds?.filter(r => r.payment_method === 'tarjeta').reduce((sum, r) => sum + r.refund_amount, 0) || 0) -
                            (operations.refunds?.filter(r => r.payment_method === 'mixto').reduce((sum, r) => sum + (r.payment_split?.tarjeta || 0), 0) || 0)
                          )}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-[10px] text-zinc-500 mb-1">Deuda</p>
                        <p className="text-sm font-mono text-amber-400">
                          {formatCurrency(
                            (operations.sales?.filter(s => !s.refunded && s.payment_method === 'deuda').reduce((sum, s) => sum + s.total, 0) || 0) +
                            (operations.sales?.filter(s => !s.refunded && s.payment_method === 'mixto').reduce((sum, s) => sum + (s.payment_split?.deuda || 0), 0) || 0)
                          )}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-[10px] text-zinc-500 mb-1">Devoluciones</p>
                        <p className="text-sm font-mono text-red-400">
                          -{formatCurrency(operations.refunds?.reduce((sum, r) => sum + r.refund_amount, 0) || 0)}
                        </p>
                      </div>
                      <div className="text-center border-l border-zinc-700">
                        <p className="text-[10px] text-zinc-500 mb-1">Total Neto</p>
                        <p className="text-sm font-mono text-white font-bold">
                          {formatCurrency(
                            (operations.sales?.filter(s => !s.refunded).reduce((sum, s) => sum + s.total, 0) || 0) +
                            (operations.debt_payments?.reduce((sum, p) => sum + p.amount, 0) || 0) -
                            (operations.refunds?.reduce((sum, r) => sum + r.refund_amount, 0) || 0)
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOperationsModal(false)} className="border-zinc-700">
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ EDIT SALE MODAL ============ */}
      <Dialog open={!!editingSale} onOpenChange={() => setEditingSale(null)}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-md z-[150]">
          <DialogHeader>
            <DialogTitle className="text-white">Editar Venta</DialogTitle>
          </DialogHeader>
          {editingSale && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Cliente</Label>
                <Input
                  value={editingSale.customer_name || ''}
                  onChange={(e) => setEditingSale({ ...editingSale, customer_name: e.target.value })}
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Descuento (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={editingSale.discount_percent || 0}
                  onChange={(e) => setEditingSale({ ...editingSale, discount_percent: parseFloat(e.target.value) || 0 })}
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Notas</Label>
                <Input
                  value={editingSale.notes || ''}
                  onChange={(e) => setEditingSale({ ...editingSale, notes: e.target.value })}
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingSale(null)} className="border-zinc-700">Cancelar</Button>
            <Button onClick={handleUpdateSale} className="bg-[#FF3B30] hover:bg-[#D32F2F]">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ EXPENSE MODAL ============ */}
      <Dialog open={showExpenseModal} onOpenChange={setShowExpenseModal}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-md mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ArrowDownCircle size={20} />
              Gasto / Retiro de Efectivo
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="expense-type-gasto"
                onClick={() => setExpenseType('gasto')}
                className={`py-3 rounded-sm font-medium transition-fast flex items-center justify-center gap-2 ${
                  expenseType === 'gasto'
                    ? 'bg-amber-600 text-white'
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                }`}
              >
                <Receipt size={18} />
                Gasto
              </button>
              <button
                data-testid="expense-type-retiro"
                onClick={() => setExpenseType('retiro')}
                className={`py-3 rounded-sm font-medium transition-fast flex items-center justify-center gap-2 ${
                  expenseType === 'retiro'
                    ? 'bg-purple-600 text-white'
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                }`}
              >
                <Banknote size={18} />
                Retiro
              </button>
            </div>

            <div>
              <Label className="text-zinc-400 mb-2 block">Monto</Label>
              <Input
                data-testid="expense-amount-input"
                type="number"
                step="0.01"
                value={expenseAmount}
                onChange={(e) => setExpenseAmount(e.target.value)}
                placeholder="0.00"
                className="bg-zinc-900 border-zinc-800 text-lg font-mono h-12"
              />
            </div>

            {expenseType === 'gasto' && (
              <div>
                <Label className="text-zinc-400 mb-2 block">Categoría</Label>
                <Select value={expenseCategory} onValueChange={setExpenseCategory}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-zinc-800">
                    {expenseCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-zinc-400 mb-2 block">Descripción</Label>
              <Input
                data-testid="expense-description-input"
                value={expenseDescription}
                onChange={(e) => setExpenseDescription(e.target.value)}
                placeholder={expenseType === 'gasto' ? 'Ej: Pago de luz' : 'Ej: Retiro para banco'}
                className="bg-zinc-900 border-zinc-800"
              />
            </div>

            <Button
              data-testid="submit-expense-btn"
              onClick={submitExpense}
              className={`w-full ${expenseType === 'gasto' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-purple-600 hover:bg-purple-700'}`}
            >
              Registrar {expenseType === 'gasto' ? 'Gasto' : 'Retiro'}
            </Button>

            {todayExpenses.length > 0 && (
              <div className="border-t border-zinc-800 pt-4">
                <h4 className="text-xs text-zinc-500 uppercase mb-3">Hoy</h4>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {todayExpenses.map((exp) => (
                    <div key={exp.id} className="flex justify-between items-center bg-zinc-900/50 p-2 rounded-sm">
                      <div>
                        <p className="text-sm text-white">{exp.description}</p>
                        <p className="text-xs text-zinc-500">{exp.type}</p>
                      </div>
                      <span className={`font-mono font-bold ${exp.type === 'gasto' ? 'text-amber-400' : 'text-purple-400'}`}>
                        -{formatCurrency(exp.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExpenseModal(false)} className="border-zinc-700">
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ CLOSE CASH SESSION MODAL ============ */}
      <Dialog open={showCloseCashModal} onOpenChange={setShowCloseCashModal}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-md mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Calculator size={20} />
              Cierre de Caja
            </DialogTitle>
          </DialogHeader>
          
          {session && !closingResult && (
            <div className="space-y-4">
              <div className="bg-zinc-900 rounded-sm p-4 space-y-2">
                <h3 className="text-xs text-zinc-500 uppercase font-semibold">Resumen de Sesión</h3>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Efectivo Inicial</span>
                  <span className="font-mono text-white">{formatCurrency(session.initial_cash)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Ventas Efectivo</span>
                  <span className="font-mono text-emerald-400">+{formatCurrency(session.current_cash_sales)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Cobros Deudas</span>
                  <span className="font-mono text-emerald-400">+{formatCurrency(session.current_debt_payments_cash)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Gastos</span>
                  <span className="font-mono text-amber-400">-{formatCurrency(session.current_expenses)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Retiros</span>
                  <span className="font-mono text-purple-400">-{formatCurrency(session.current_withdrawals)}</span>
                </div>
                <div className="border-t border-zinc-800 pt-2 mt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Ventas Tarjeta</span>
                    <span className="font-mono text-blue-400">{formatCurrency(session.current_card_sales)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Ventas Deuda</span>
                    <span className="font-mono text-red-400">{formatCurrency(session.current_debt_sales)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-emerald-600/10 border border-emerald-600/30 rounded-sm p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs text-emerald-400 uppercase">Efectivo Esperado</p>
                    <p className="text-2xl font-mono font-bold text-emerald-400">
                      {formatCurrency(session.current_expected_cash)}
                    </p>
                  </div>
                  <Banknote size={32} className="text-emerald-600" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-zinc-400">Efectivo Contado</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => openCalculator('close')}
                    className="h-8 px-2 text-amber-400 hover:text-amber-300 hover:bg-amber-400/10"
                  >
                    <Calculator size={16} className="mr-1" />
                    Contar
                  </Button>
                </div>
                <Input
                  data-testid="counted-cash-input"
                  type="number"
                  step="0.01"
                  value={countedCash}
                  onChange={(e) => setCountedCash(e.target.value)}
                  className="bg-zinc-900 border-zinc-800 text-xl font-mono h-14"
                />
              </div>

              {countedCash && (
                <div className={`rounded-sm p-3 ${
                  Math.abs(parseFloat(countedCash) - session.current_expected_cash) < 1
                    ? 'bg-emerald-600/10 border border-emerald-600/30'
                    : parseFloat(countedCash) > session.current_expected_cash
                    ? 'bg-blue-600/10 border border-blue-600/30'
                    : 'bg-red-600/10 border border-red-600/30'
                }`}>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-zinc-400">Diferencia</span>
                    <span className={`font-mono font-bold ${
                      Math.abs(parseFloat(countedCash) - session.current_expected_cash) < 1
                        ? 'text-emerald-400'
                        : parseFloat(countedCash) > session.current_expected_cash
                        ? 'text-blue-400'
                        : 'text-red-400'
                    }`}>
                      {parseFloat(countedCash) >= session.current_expected_cash ? '+' : ''}
                      {formatCurrency(parseFloat(countedCash) - session.current_expected_cash)}
                    </span>
                  </div>
                </div>
              )}

              <div>
                <Label className="text-zinc-400 mb-2 block">Notas (opcional)</Label>
                <Input
                  value={closeNotes}
                  onChange={(e) => setCloseNotes(e.target.value)}
                  placeholder="Observaciones..."
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
            </div>
          )}

          {closingResult && (
            <div className={`rounded-sm p-6 text-center ${
              closingResult.status === 'ok' 
                ? 'bg-emerald-600/10 border border-emerald-600/30'
                : closingResult.status === 'surplus'
                ? 'bg-blue-600/10 border border-blue-600/30'
                : 'bg-red-600/10 border border-red-600/30'
            }`}>
              {closingResult.status === 'ok' ? (
                <CheckCircle size={48} className="mx-auto text-emerald-500 mb-3" />
              ) : (
                <AlertTriangle size={48} className={`mx-auto mb-3 ${closingResult.status === 'surplus' ? 'text-blue-500' : 'text-red-500'}`} />
              )}
              
              <h3 className="text-lg font-bold text-white mb-2">
                {closingResult.status === 'ok' ? '¡Caja Cuadrada!' : closingResult.status === 'surplus' ? 'Sobrante' : 'Faltante'}
              </h3>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Esperado</span>
                  <span className="font-mono text-white">{formatCurrency(closingResult.expected_cash)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Contado</span>
                  <span className="font-mono text-white">{formatCurrency(closingResult.counted_cash)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-zinc-700">
                  <span className="text-zinc-400">Diferencia</span>
                  <span className={`font-mono font-bold ${closingResult.status === 'ok' ? 'text-emerald-400' : closingResult.status === 'surplus' ? 'text-blue-400' : 'text-red-400'}`}>
                    {closingResult.difference >= 0 ? '+' : ''}{formatCurrency(closingResult.difference)}
                  </span>
                </div>
                <div className="flex justify-between pt-2">
                  <span className="text-zinc-400">Total Facturado</span>
                  <span className="font-mono font-bold text-white">{formatCurrency(closingResult.total_sales)}</span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowCloseCashModal(false); setClosingResult(null); }}
              className="border-zinc-700 w-full sm:w-auto"
            >
              {closingResult ? 'Cerrar' : 'Cancelar'}
            </Button>
            {!closingResult && (
              <Button
                data-testid="confirm-close-cash-btn"
                onClick={handleCloseCash}
                className="bg-[#FF3B30] hover:bg-[#D32F2F] w-full sm:w-auto"
              >
                Cerrar Caja
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Calculator Modal */}
      {renderCalculatorModal()}
      
      {/* ============ SPLIT PAYMENT MODAL ============ */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Wallet size={20} className="text-purple-400" />
              Dividir Pago
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Total to pay */}
            <div className="bg-zinc-900/50 p-4 rounded-sm text-center">
              <p className="text-xs text-zinc-500 uppercase mb-1">Total a Pagar</p>
              <p className="text-3xl font-mono font-bold text-[#FF3B30]">{formatCurrency(total)}</p>
            </div>
            
            {/* Split fields */}
            <div className="space-y-3">
              {/* Efectivo */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-emerald-400 flex items-center gap-2">
                    <Banknote size={16} />
                    Efectivo
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => fillRemaining('efectivo')}
                    className="h-6 px-2 text-xs text-zinc-400 hover:text-emerald-400"
                  >
                    Restante
                  </Button>
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">€</span>
                  <Input
                    data-testid="split-efectivo"
                    type="number"
                    step="0.01"
                    min="0"
                    value={splitPayment.efectivo}
                    onChange={(e) => setSplitPayment(prev => ({ ...prev, efectivo: e.target.value }))}
                    placeholder="0.00"
                    className="pl-8 bg-zinc-900 border-zinc-800 focus:border-emerald-500 font-mono"
                  />
                </div>
              </div>
              
              {/* Tarjeta */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-blue-400 flex items-center gap-2">
                    <CreditCard size={16} />
                    Tarjeta
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => fillRemaining('tarjeta')}
                    className="h-6 px-2 text-xs text-zinc-400 hover:text-blue-400"
                  >
                    Restante
                  </Button>
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">€</span>
                  <Input
                    data-testid="split-tarjeta"
                    type="number"
                    step="0.01"
                    min="0"
                    value={splitPayment.tarjeta}
                    onChange={(e) => setSplitPayment(prev => ({ ...prev, tarjeta: e.target.value }))}
                    placeholder="0.00"
                    className="pl-8 bg-zinc-900 border-zinc-800 focus:border-blue-500 font-mono"
                  />
                </div>
              </div>
              
              {/* Deuda */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-red-400 flex items-center gap-2">
                    <Clock size={16} />
                    Deuda
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => fillRemaining('deuda')}
                    className="h-6 px-2 text-xs text-zinc-400 hover:text-red-400"
                  >
                    Restante
                  </Button>
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">€</span>
                  <Input
                    data-testid="split-deuda"
                    type="number"
                    step="0.01"
                    min="0"
                    value={splitPayment.deuda}
                    onChange={(e) => setSplitPayment(prev => ({ ...prev, deuda: e.target.value }))}
                    placeholder="0.00"
                    className="pl-8 bg-zinc-900 border-zinc-800 focus:border-red-500 font-mono"
                  />
                </div>
              </div>
            </div>
            
            {/* Summary */}
            <div className="border-t border-zinc-800 pt-3">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-zinc-400">Total ingresado:</span>
                <span className={`font-mono font-bold ${
                  Math.abs((parseFloat(splitPayment.efectivo || 0) + parseFloat(splitPayment.tarjeta || 0) + parseFloat(splitPayment.deuda || 0)) - total) < 0.01
                    ? 'text-emerald-400'
                    : 'text-amber-400'
                }`}>
                  {formatCurrency(parseFloat(splitPayment.efectivo || 0) + parseFloat(splitPayment.tarjeta || 0) + parseFloat(splitPayment.deuda || 0))}
                </span>
              </div>
              {Math.abs((parseFloat(splitPayment.efectivo || 0) + parseFloat(splitPayment.tarjeta || 0) + parseFloat(splitPayment.deuda || 0)) - total) > 0.01 && (
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Diferencia:</span>
                  <span className="font-mono text-amber-400">
                    {formatCurrency(total - (parseFloat(splitPayment.efectivo || 0) + parseFloat(splitPayment.tarjeta || 0) + parseFloat(splitPayment.deuda || 0)))}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPaymentModal(false)}
              className="border-zinc-700 text-zinc-300"
            >
              Cancelar
            </Button>
            <Button
              data-testid="confirm-split-payment"
              onClick={handleSplitPayment}
              disabled={processing || Math.abs((parseFloat(splitPayment.efectivo || 0) + parseFloat(splitPayment.tarjeta || 0) + parseFloat(splitPayment.deuda || 0)) - total) > 0.01}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
            >
              {processing ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <>
                  <CheckCircle size={18} className="mr-2" />
                  Confirmar Pago
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* ============ REFUND CONFIRMATION MODAL ============ */}
      <Dialog open={showRefundModal} onOpenChange={setShowRefundModal}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-md mx-4 z-[150]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <RotateCcw size={20} className="text-amber-400" />
              Confirmar Devolución
            </DialogTitle>
          </DialogHeader>
          
          {refundingSale && (
            <div className="space-y-4 py-4">
              {/* Sale details */}
              <div className="bg-zinc-900/50 p-4 rounded-sm">
                <p className="text-xs text-zinc-500 uppercase mb-2">Venta a Devolver</p>
                <div className="space-y-1">
                  {refundingSale.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-zinc-300">{item.quantity}x {item.item_name}</span>
                      <span className="text-zinc-400">{formatCurrency(item.subtotal)}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-zinc-700 mt-3 pt-3 flex justify-between">
                  <span className="text-white font-medium">Total a Devolver:</span>
                  <span className="text-xl font-mono font-bold text-red-400">{formatCurrency(refundingSale.total)}</span>
                </div>
              </div>
              
              {/* Reason input */}
              <div>
                <Label className="text-zinc-400 mb-2 block">Razón de la devolución (opcional)</Label>
                <Input
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="Ej: Producto defectuoso, Cliente cambió de opinión..."
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
              
              {/* Warning */}
              <div className="flex items-start gap-2 p-3 bg-amber-900/20 border border-amber-800/50 rounded-sm">
                <AlertTriangle size={16} className="text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-400">
                  <p className="font-medium">Esta acción no se puede deshacer.</p>
                  <p className="text-amber-400/70 mt-1">El stock se restaurará y el efectivo se deducirá del arqueo de caja.</p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowRefundModal(false)}
              className="border-zinc-700 text-zinc-300"
              disabled={refundProcessing}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleRefundSale}
              disabled={refundProcessing}
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold"
            >
              {refundProcessing ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <>
                  <RotateCcw size={18} className="mr-2" />
                  Confirmar Devolución
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
  
  // Cash Calculator Modal Component
  function renderCalculatorModal() {
    const denominations = [
      { key: 'b100', label: '€100', value: 100, color: 'text-emerald-400', bg: 'bg-emerald-600/10' },
      { key: 'b50', label: '€50', value: 50, color: 'text-orange-400', bg: 'bg-orange-600/10' },
      { key: 'b20', label: '€20', value: 20, color: 'text-blue-400', bg: 'bg-blue-600/10' },
      { key: 'b10', label: '€10', value: 10, color: 'text-pink-400', bg: 'bg-pink-600/10' },
      { key: 'b5', label: '€5', value: 5, color: 'text-cyan-400', bg: 'bg-cyan-600/10' },
      { key: 'c2', label: '€2', value: 2, color: 'text-amber-400', bg: 'bg-amber-600/10' },
      { key: 'c1', label: '€1', value: 1, color: 'text-yellow-400', bg: 'bg-yellow-600/10' },
      { key: 'c050', label: '€0,50', value: 0.50, color: 'text-lime-400', bg: 'bg-lime-600/10' },
      { key: 'c020', label: '€0,20', value: 0.20, color: 'text-zinc-300', bg: 'bg-zinc-600/10' },
      { key: 'c010', label: '€0,10', value: 0.10, color: 'text-zinc-400', bg: 'bg-zinc-700/10' },
      { key: 'c005', label: '€0,05', value: 0.05, color: 'text-red-400', bg: 'bg-red-600/10' },
      { key: 'c002', label: '€0,02', value: 0.02, color: 'text-teal-400', bg: 'bg-teal-600/10' },
      { key: 'c001', label: '€0,01', value: 0.01, color: 'text-purple-400', bg: 'bg-purple-600/10' },
    ];

    return (
      <Dialog open={showCalculator} onOpenChange={setShowCalculator}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-md mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Calculator size={20} className="text-amber-400" />
              Contador de Efectivo
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            {/* Total Display */}
            <div className="bg-emerald-600/10 border border-emerald-600/30 rounded-sm p-4 text-center">
              <p className="text-xs text-emerald-400 uppercase mb-1">Total Contado</p>
              <p className="text-3xl font-mono font-bold text-emerald-400">
                {formatCurrency(calculateTotal())}
              </p>
            </div>
            
            {/* Bills Section */}
            <div>
              <p className="text-xs text-zinc-500 uppercase mb-2 font-semibold">Billetes</p>
              <div className="grid grid-cols-2 gap-2">
                {denominations.slice(0, 5).map(d => (
                  <div key={d.key} className={`${d.bg} rounded-sm p-2 flex items-center justify-between`}>
                    <span className={`font-mono font-bold ${d.color}`}>{d.label}</span>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => updateBillCount(d.key, -1)}
                        className="h-7 w-7 p-0 text-zinc-400 hover:text-white"
                      >
                        <Minus size={14} />
                      </Button>
                      <input
                        type="number"
                        min="0"
                        value={billCounts[d.key]}
                        onChange={(e) => setBillCounts(prev => ({
                          ...prev,
                          [d.key]: Math.max(0, parseInt(e.target.value) || 0)
                        }))}
                        className="w-12 text-center font-mono text-white bg-transparent border border-zinc-700 rounded px-1 py-0.5 focus:border-amber-400 focus:outline-none"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => updateBillCount(d.key, 1)}
                        className="h-7 w-7 p-0 text-zinc-400 hover:text-white"
                      >
                        <Plus size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Coins Section */}
            <div>
              <p className="text-xs text-zinc-500 uppercase mb-2 font-semibold">Monedas</p>
              <div className="grid grid-cols-2 gap-2">
                {denominations.slice(5).map(d => (
                  <div key={d.key} className={`${d.bg} rounded-sm p-2 flex items-center justify-between`}>
                    <span className={`font-mono font-bold ${d.color}`}>{d.label}</span>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => updateBillCount(d.key, -1)}
                        className="h-7 w-7 p-0 text-zinc-400 hover:text-white"
                      >
                        <Minus size={14} />
                      </Button>
                      <input
                        type="number"
                        min="0"
                        value={billCounts[d.key]}
                        onChange={(e) => setBillCounts(prev => ({
                          ...prev,
                          [d.key]: Math.max(0, parseInt(e.target.value) || 0)
                        }))}
                        className="w-12 text-center font-mono text-white bg-transparent border border-zinc-700 rounded px-1 py-0.5 focus:border-amber-400 focus:outline-none"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => updateBillCount(d.key, 1)}
                        className="h-7 w-7 p-0 text-zinc-400 hover:text-white"
                      >
                        <Plus size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowCalculator(false)}
              className="border-zinc-700 w-full sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              onClick={applyCalculatorResult}
              className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
            >
              Aplicar {formatCurrency(calculateTotal())}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
};

export default POS;
